/**
 * Reserve privacy indicator: per-asset labels estimated locally from public
 * chain data, following "Redacted Reserve Privacy Indicator Exploration"
 * (XXXXXX, 2026-09-15). Labels, never a percentage or a promise of anonymity;
 * nothing here ever leaves the browser.
 */

/**
 * - `unknown`: not enough data (reads failed, history incomplete). Never shown
 *   as good.
 * - `limited`: public data points to this user or narrows the alternatives a
 *   lot (see reasons).
 * - `no_known_link`: none of the checked signals finds a link. Not "safe":
 *   the model does not yet count plausible alternatives.
 */
export type V2PrivacyLevel = "unknown" | "limited" | "no_known_link";

/** Why an asset got its level; shown as plain-language reasons. */
export type V2PrivacyReason =
  /** Nobody else holds this asset privately right now. */
  | "only_holder"
  /** This user's amount is larger than everyone else's combined: a move above
   * the others' total can only come from this user. */
  | "largest_holder"
  /** Very few other unspent notes exist in the pool. */
  | "small_pool"
  /** A note's encrypted size is shared by no other note (size leaks the
   * amount's digit count). */
  | "unique_note_size"
  /** Part of the balance came from the user's own merge, change or return of
   * funds that were limited: moving one's own funds never improves a label. */
  | "carried_over"
  /** Part of the balance still sits in the public Spending account. */
  | "partly_public"
  /** Nothing of the asset is in the Reserve: the whole balance sits in the
   * public Spending account (or is on its way back from it). */
  | "spending_only"
  /** The pool totals could not be read or changed while reading. */
  | "data_unavailable";

export type V2PrivacyAssessment = {
  /** Native denom, e.g. "rune" or "btc-btc". */
  denom: string;
  level: V2PrivacyLevel;
  /** The asset's notes differ in exposure (some limited, some not). */
  mixed: boolean;
  /** Part of the balance is in the public Spending account (never averaged
   * away; shown next to the level). */
  partlyPublic: boolean;
  /** Separate warning: an account link was detected. Always false in v1 (no
   * registration-linkage replay yet); never overridden by a level. */
  linkDetected: boolean;
  reasons: readonly V2PrivacyReason[];
};

export type V2PrivacyState = {
  /** Per native denom the user holds privately. */
  byDenom: ReadonlyMap<string, V2PrivacyAssessment>;
  /** An assessment is being computed (keep showing the previous one). */
  checking: boolean;
  /** When the current assessments were computed (ms since epoch). */
  updatedAt?: number;
};

export const EMPTY_PRIVACY_STATE: V2PrivacyState = {
  byDenom: new Map(),
  checking: false,
};
